import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, TextField, InputAdornment, Chip, IconButton, Tooltip, MenuItem, Select, Switch, FormControlLabel, Autocomplete, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
// import DeleteIcon from '@mui/icons-material/Delete';
import { SearchIcon } from 'lucide-react';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSubGroups, fetchGroupCategories, fetchPrimaryGroups } from '../../store/coaStore/coaThunk';
// import { deleteSubGroup } from '../../store/coaStore/coaThunk';
import { ApiStatus, defaultRowsPerPageOptions } from '../../utils/api.types';
import CustomDataGrid from '../../components/DataGrid/DataGrid';
import SubGroupForm from '../../components/COA/SubGroupForm';
import { ISubGroup, IPrimaryGroup } from '../../utils/coa.types';

const SubGroups: React.FC = () => {
  const dispatch = useAppDispatch();
  const { subGroups, subGroupsTotal, groupCategories, primaryGroups, status } = useAppSelector(state => state.coa);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupCategoryId, setGroupCategoryId] = useState('');
  const [primaryGroupId, setPrimaryGroupId] = useState('');
  const [selectedPrimaryGroup, setSelectedPrimaryGroup] = useState<IPrimaryGroup | null>(null);
  const [filterOwn, setFilterOwn] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState<ISubGroup | null>(null);

  useEffect(() => {
    dispatch(fetchGroupCategories({ page: 0, limit: 1000 }));
    dispatch(fetchPrimaryGroups({ page: 0, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchSubGroups({ page, limit: pageSize, search: searchQuery, groupCategoryId, primaryGroupId, filterOwn }));
  }, [dispatch, page, pageSize, searchQuery, groupCategoryId, primaryGroupId, filterOwn]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handleCreate = () => {
    setEditData(null);
    setFormOpen(true);
  };

  const handleEdit = (row: ISubGroup) => {
    setEditData(row);
    setFormOpen(true);
  };

  // const handleDelete = async (id: string) => {
  //   if (window.confirm('Are you sure you want to delete this Sub Group?')) {
  //     try {
  //       await dispatch(deleteSubGroup(id)).unwrap();
  //       toast.success('Sub Group deleted successfully');
  //       dispatch(fetchSubGroups({ page, limit: pageSize, search: searchQuery }));
  //     } catch (error: any) {
  //       toast.error(error?.message || 'Failed to delete');
  //     }
  //   }
  // };

  const handleFormClose = (refresh?: boolean) => {
    setFormOpen(false);
    setEditData(null);
    if (refresh) {
      dispatch(fetchSubGroups({ page, limit: pageSize, search: searchQuery }));
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'seqNumber',
      headerName: 'Seq',
      flex: 0.5,
      renderCell: ({ row }: GridValueGetterParams) => row.seqNumber || '-'
    },
    {
      field: 'accountingCode',
      headerName: 'Code',
      flex: 0.8,
      renderCell: ({ row }: GridValueGetterParams) => row.accountingCode || '-'
    },
    {
      field: 'groupCategory',
      headerName: 'Category',
      flex: 1.2,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Chip
          size="small"
          label={row.groupCategory?.name || '-'}
          style={{ borderRadius: 7, color: '#000000', backgroundColor: '#f3e5f5' }}
        />
      )
    },
    {
      field: 'primaryGroup',
      headerName: 'Primary Group',
      flex: 1.2,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Chip
          size="small"
          label={row.primaryGroup?.name || '-'}
          style={{ borderRadius: 7, color: '#000000', backgroundColor: '#f1f0fc' }}
        />
      )
    },
    {
      field: 'name',
      headerName: 'Subgroup',
      flex: 2,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.name}>
          <strong>{row.name}</strong>
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      renderCell: ({ row }: GridValueGetterParams) => {
        // Check if sub group is global (can only edit own sub groups, not global ones)
        const isGlobalSubGroup = !row.account;
        const canEdit = !isGlobalSubGroup;

        return (
          <Tooltip title={!canEdit ? "Cannot edit global sub group" : ""}>
            <span>
              <IconButton
                onClick={() => canEdit && handleEdit(row)}
                disabled={!canEdit}
                style={{
                  color: canEdit ? '#49a563' : '#9e9e9e',
                  backgroundColor: canEdit ? '#edf6ef' : '#f5f5f5',
                  border: canEdit ? '0.1px solid #49a563' : '0.1px solid #9e9e9e',
                  height: 35,
                  width: 35,
                  cursor: canEdit ? 'pointer' : 'not-allowed'
                }}
                size="small"
              >
                <EditIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        );
      }
    }
  ], []);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: '100%', pt: 2, pb: 1, gap: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: '100%', gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <TextField
              className='c-text-field-3'
              variant="standard"
              style={{ width: 300, float: 'left' }}
              size='small'
              placeholder='Search Sub Groups...'
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ marginTop: -3 }} size={20} />
                  </InputAdornment>
                ),
                style: { fontSize: 14, fontWeight: 'normal' },
              }}
            />
            <Select
              name="groupCategory"
              displayEmpty
              disableUnderline
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              sx={{
                boxShadow: "none",
                width: 200,
                ml: '10px !important',
                pt: '0px !important',
                pl: '10px !important',
                '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                '& .MuiSelect-select': { p: 0 },
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
              }}
              className='c-text-field-3'
              variant="outlined"
              value={groupCategoryId}
              onChange={(e) => {
                setGroupCategoryId(e.target.value);
                setPage(0);
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#999' }}>All Categories</span>;
                }
                const category = groupCategories.find(cat => cat._id === selected);
                return category?.name || 'All Categories';
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {groupCategories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            <Autocomplete
              options={primaryGroups}
              getOptionLabel={(option) => option.name || ''}
              value={selectedPrimaryGroup}
              onChange={(event, newValue) => {
                setSelectedPrimaryGroup(newValue);
                setPrimaryGroupId(newValue?._id || '');
                setPage(0);
              }}
              sx={{
                width: 200,
                ml: '10px !important',
                '& .MuiOutlinedInput-root': {
                  padding: '0px !important',
                  paddingLeft: '10px !important',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiAutocomplete-input': {
                  padding: '0px !important',
                  fontSize: 14,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Primary Groups"
                  variant="outlined"
                  className='c-text-field-3'
                />
              )}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filterOwn}
                  onChange={(e) => {
                    setFilterOwn(e.target.checked);
                    setPage(0);
                  }}
                  sx={{
                    ml: 2,
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#488dff',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#488dff',
                    },
                  }}
                />
              }
              label="My Sub Groups"
              sx={{ ml: 2, color: '#666' }}
            />
          </Box>
          <Button
            startIcon={<AddIcon />}
            size='small'
            onClick={handleCreate}
            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
            variant="contained"
          >
            Add Sub Group
          </Button>
        </Box>
      </Box>

      <div style={{ height: "calc(100vh - 200px)", width: "100%" }}>
        {subGroups.length === 0 && status !== ApiStatus.LOADING ? (
          <Paper
            elevation={0}
            sx={{
              height: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fafafb',
              border: '2px dashed #e0e0e0',
              borderRadius: 2,
              p: 4,
              marginTop: '10px'
            }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
              {/* Icon */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 3,
                  mt: 1
                }}
              >
                <FolderIcon sx={{ fontSize: 60, color: 'primary.main' }} />
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mb: 1
                }}
              >
                No Sub Groups Yet
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Start organizing your accounts by creating your first sub group.
                Examples: Current Assets, Fixed Assets, Current Liabilities, etc.
              </Typography>

              {/* CTA Button */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: (theme) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                    '&::before': {
                      transform: 'translateX(0)',
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease',
                    zIndex: 1
                  },
                  '&:active': {
                    transform: 'translateY(-2px) scale(0.98)',
                  },
                  '& .MuiButton-startIcon': {
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover .MuiButton-startIcon': {
                    transform: 'rotate(360deg)',
                  }
                }}
              >
                Add Your First Sub Group
              </Button>

              {/* Additional Help Text */}
              {/* <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  mb: 4,
                  color: '#999'
                }}
              >
                Sub groups help you further categorize accounts within primary groups
              </Typography> */}
            </Box>
          </Paper>
        ) : (
          <CustomDataGrid
            columns={columns}
            rows={subGroups}
            rowCount={subGroupsTotal}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            getRowId={(row) => row._id}
            loading={status === ApiStatus.LOADING}
          />
        )}
      </div>

      <SubGroupForm
        open={formOpen}
        onClose={handleFormClose}
        editData={editData}
      />
    </>
  );
};

export default SubGroups;
